import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { HeartHandshake, CheckCircle2, XCircle, ShieldOff } from "lucide-react";
import { toast } from "sonner";

// Phase 18-A2 item 2: admin — referrer approval queue, active referrers with
// revoke, and referral analytics counts.
export function ReferrersQueue() {
  const [data, setData] = useState({ pending: [], active: [] });
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => {
    api.adminReferrers().then(setData).catch(() => {});
    api.referralAnalytics().then(setStats).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const decide = async (a, action) => {
    setBusy(a.id);
    try {
      if (action === "approve") await api.approveReferrer(a.id);
      else await api.rejectReferrer(a.id);
      toast.success(action === "approve" ? `${a.name} is now a referrer` : "Application rejected");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    setBusy(null);
  };

  const revoke = async (u) => {
    try {
      await api.revokeReferrer(u.user_id);
      toast.success(`Referrer access revoked for ${u.name}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not revoke"); }
  };

  return (
    <div className="mt-14" data-testid="admin-referrers">
      <h2 className="font-display text-2xl text-forest flex items-center gap-2">
        <HeartHandshake className="w-5 h-5 text-terracotta" /> Professional referrers
      </h2>
      {stats && (
        <div className="text-sm text-neutral-600 mt-1" data-testid="referral-analytics">
          Referrals: <b>{stats.referrals.open}</b> open · <b>{stats.referrals.fulfilled}</b> fulfilled · <b>{stats.referrals.expired}</b> expired
          &nbsp;— Invites: <b>{stats.invites.converted}</b>/<b>{stats.invites.total}</b> converted
        </div>
      )}

      {data.pending.length === 0 ? (
        <div className="text-sm text-neutral-500 mt-4">No applications waiting.</div>
      ) : (
        <div className="mt-4 space-y-3">
          {data.pending.map((a) => (
            <Card key={a.id} data-testid={`referrer-app-${a.id}`} className="border-amber-200 bg-amber-50/40">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-medium text-forest">{a.name} <span className="text-neutral-500 font-normal">· {a.role}</span></div>
                    <div className="text-sm text-neutral-600">{a.organisation} · {a.work_email}</div>
                  </div>
                  <Badge className="bg-amber-100 text-amber-800 border-none">Pending</Badge>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button data-testid={`referrer-approve-${a.id}`} disabled={busy === a.id} onClick={() => decide(a, "approve")} className="rounded-full bg-forest hover:bg-forest-hover h-8 px-4 text-xs">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                  </Button>
                  <Button data-testid={`referrer-reject-${a.id}`} disabled={busy === a.id} onClick={() => decide(a, "reject")} variant="outline" className="rounded-full h-8 px-4 text-xs">
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {data.active.length > 0 && (
        <div className="mt-5 space-y-2">
          {data.active.map((u) => (
            <div key={u.user_id} data-testid={`referrer-active-${u.user_id}`} className="flex items-center justify-between gap-3 bg-white border border-neutral-200 rounded-xl px-4 py-2.5">
              <div className="text-sm text-forest font-medium">{u.name} <span className="text-neutral-500 font-normal">· {u.email}</span></div>
              <Button data-testid={`referrer-revoke-${u.user_id}`} onClick={() => revoke(u)} variant="outline" className="rounded-full h-8 px-3 text-xs">
                <ShieldOff className="w-3.5 h-3.5 mr-1" /> Revoke
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
